import { Resources } from 'constants/resources';

import ContentAdd from '@mui/icons-material/Add';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { ListContextProvider, useListController } from 'react-admin';
import { Alert } from 'shared/mui/Alert/Alert';
import { LinkButton } from 'shared/mui/Buttons';
import {
  ContextMenu,
  ContextMenuDeleteButton,
  ContextMenuEditButton,
  ContextMenuEntityUserActionsButton,
  ContextMenuShowButton,
} from 'shared/mui/ContextMenu';
import { PageHeader } from 'shared/mui/PageHeader';
import { DatagridSkeleton } from 'shared/react-admin/Datagrids';
import { DateTimeField, TextField } from 'shared/react-admin/Fields';
import { Pagination } from 'shared/react-admin/Pagination/Pagination';
import { Toolbar } from 'shared/react-admin/Toolbars/Toolbar';
import { GatewayRule } from 'types/embedded/gatewayRule';
import { Permission } from 'types/embedded/permission';
import { convertReactAdminIdToHydraId } from 'utils';

import { MerchantShowRulesDeleteDialog } from './components/MerchantShowRulesDeleteDialog';
import { MerchantShowRulesNameField } from './components/MerchantShowRulesNameField';
import { MerchantShowRulesListFilter } from './MerchantShowRulesListFilter';
import { StyledDatagrid } from './MerchantShowRulesListStyled.styled';
import { MerchantsShowActionSkeleton } from '../components/MerchantsShowActionSkeleton';
import { MerchantsShowFiltersSkeleton } from '../components/MerchantsShowFiltersSkeleton';

type Props = {
  merchantId?: string;
  merchantName?: string;
};

export const MerchantShowRulesList: React.FC<Props> = ({ merchantId, merchantName }) => {
  const translate = useTypedTranslate();
  const { hasResourcePermissions, hasResourceAccess } = usePermissions();

  const resource = `${Resources.Merchants}/${merchantId}/gateway_rules`;
  const listContext = useListController({
    resource,
    sort: { field: 'id', order: 'DESC' },
    disableSyncWithLocation: true,
  });

  const {
    record: recordDeleteDialog,
    handleCloseRecordModal: handleCloseDeleteDialog,
    openRecordModal: openDeleteDialog,
    handleOpenRecordModal: handleOpenDeleteDialog,
  } = useRecordModal<GatewayRule>();

  const isHaveData = !!listContext.data?.length;
  const hasFilters = !!Object.keys(listContext.filterValues).length;
  const shouldShowContent = isHaveData || listContext.isLoading || hasFilters;

  return (
    <ListContextProvider value={listContext}>
      <PageHeader
        actions={[
          hasResourcePermissions(Resources.GatewayRules, [Permission.Full, Permission.Create]) &&
            (listContext.isLoading ? (
              <MerchantsShowActionSkeleton />
            ) : (
              <LinkButton
                key="create-rule"
                label={translate('app.buttons.add')}
                pathParams={{
                  resource: Resources.GatewayRules,
                  type: 'create',
                }}
                queryParams={{
                  filter: {
                    merchant: convertReactAdminIdToHydraId(merchantId || '', 'merchants'),
                  },
                }}
                startIcon={<ContentAdd />}
                target="_blank"
                variant="contained"
              />
            )),
        ]}
        headerTitle={{
          titleText: translate('app.merchants.rulesOfMerchant'),
          titleSize: 'smallBold',
        }}
      />
      {shouldShowContent ? (
        <>
          {listContext.isLoading ? (
            <MerchantsShowFiltersSkeleton />
          ) : (
            <MerchantShowRulesListFilter />
          )}
          {listContext.isLoading ? (
            <DatagridSkeleton columnsCount={10} />
          ) : (
            <>
              <StyledDatagrid>
                <TextField label="ID" sortable={false} source="originId" />
                <MerchantShowRulesNameField
                  label={translate('app.table.name')}
                  queryKey={resource}
                  source="name"
                />
                <DateTimeField label={translate('app.table.createdAt')} source="createdAt" />
                <DateTimeField label={translate('app.table.updatedAt')} source="updatedAt" />
                <ContextMenu
                  render={(record: GatewayRule) => [
                    hasResourceAccess(Resources.UserActions) && (
                      <ContextMenuEntityUserActionsButton
                        entityId={record?.originId}
                        entityName="gateway_rule"
                        key="user-action-link"
                      />
                    ),
                    <ContextMenuShowButton
                      key="show-button"
                      linkProps={{ target: '_blank', resource: Resources.GatewayRules }}
                    />,
                    hasResourcePermissions(Resources.GatewayRules, [
                      Permission.Full,
                      Permission.Update,
                    ]) && (
                      <ContextMenuEditButton
                        key="edit-button"
                        linkProps={{
                          resource: Resources.GatewayRules,
                          target: '_blank',
                        }}
                      />
                    ),
                    hasResourcePermissions(Resources.GatewayRules, [
                      Permission.Full,
                      Permission.Delete,
                    ]) && (
                      <ContextMenuDeleteButton
                        key="delete-button"
                        onClick={() => handleOpenDeleteDialog(record)}
                      />
                    ),
                  ]}
                />
              </StyledDatagrid>
              <Toolbar style={{ paddingLeft: 0, paddingRight: 0 }}>
                <Pagination sx={{ width: '100%' }} />
              </Toolbar>
            </>
          )}
        </>
      ) : (
        <Alert severity="info">
          {translate('app.merchants.rulesHaveNotBeen', { merchantName })}
        </Alert>
      )}
      <MerchantShowRulesDeleteDialog
        onClose={handleCloseDeleteDialog}
        open={openDeleteDialog}
        queryKey={resource}
        record={recordDeleteDialog}
      />
    </ListContextProvider>
  );
};
